import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PricingOption, { Plan } from 'components/feature/PricingOption/Index';
import Button from 'components/Button/Index';
import { SubscribtionPlansService } from 'services/subscribtionPlans/subscribtionPlans';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useSnackbar } from 'notistack';

import './Index.scss';

interface UpgradePlanPopupProps {
  t: TFunction;
  selectedPlan?: Plan;
  asPage?: boolean;
}

const UpgradePlanPopup: FC<UpgradePlanPopupProps> = ({
  t,
  selectedPlan,
  asPage = false
}): JSX.Element => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { companies } = useSelector((state: RootState) => state.company);
  const currentPlan = String(companies?.subscription?.plan || 'Free');
  const isFreeUser = currentPlan === 'Free';
  const { enqueueSnackbar } = useSnackbar();

  const handleGetPlanData = async () => {
    try {
      setLoading(true);
      const response = await SubscribtionPlansService.getPlans();
      setPlans(response);
    } catch (e) {
      console.error('Error fetching plans:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetPlanData();
  }, []);

  const handleRedirect = () => {
    navigate('/membership-pricing');
  };

  const handleManageSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await SubscribtionPlansService.getSubscriptionPortal();

      if (response?.url) {
        window.location.href = response.url;
      } else {
        enqueueSnackbar(
          t('Failed to access subscription portal. Please try again.'),
          {
            variant: 'error'
          }
        );
      }
    } catch (error) {
      console.error('Error accessing subscription portal:', error);
      enqueueSnackbar(
        t('Failed to access subscription portal. Please try again.'),
        {
          variant: 'error'
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`plan${asPage ? ' plan--page' : ''}`}>
      <div className="plan__options">
        {loading ? (
          <div className="plan__loading">{t('Loading plans...')}</div>
        ) : (
          plans.map((plan) => <PricingOption key={plan.id} t={t} plan={plan} />)
        )}
      </div>

      {!isFreeUser && (
        <div className="plan__manage-subscription">
          <Button
            className="manage-subscription-button"
            t={t}
            title={t('Manage My Subscription')}
            onClick={handleManageSubscription}
            disabled={isLoading}
          />
          <p className="manage-subscription-disclaimer">
            {t('If you upgrade, downgrade, or cancel your subscription, the change will take effect at the end of your current billing period. If you cancel, your account will automatically go back to the free plan.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default UpgradePlanPopup;
